import React from "react";
import ReactDOM from "react-dom";
import { X, Paperclip } from "lucide-react";

const formatDate = (dateString) => {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (isNaN(d)) return dateString;
  return d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
};

const formatCurrency = (val) => {
  const n = Number(val || 0);
  return `₱${n.toLocaleString("en-PH", { minimumFractionDigits: 0 })}`;
};

const MIN_ROWS = 10;

const SalesOrderViewModal = ({
  isOpen,
  onClose,
  soItems = [],
  soNo = "SO-26-01001A",
  order = {},
  managerApproval = {},
  snapshot = {},
}) => {
  if (!isOpen) return null;

  const {
    processedBy = "",
    certifiedCorrect = "",
    receivedPayment = "",
    remarks = "",
    attachment = null,
  } = snapshot;

  const totalQty = soItems.reduce((s, i) => s + Number(i.quantity ?? i.qty ?? 0), 0);
  const totalPrice = soItems.reduce(
    (s, i) => s + Number(i.quantity ?? i.qty ?? 1) * Number(i.unitPrice ?? 0),
    0
  );
  const emptyCount = Math.max(0, MIN_ROWS - soItems.length);

  return ReactDOM.createPortal(
    <div className="edn-modal-overlay" onClick={onClose}>
      <div className="edn-modal-container" onClick={(e) => e.stopPropagation()}>

        <div className="edn-modal-header">
          <span className="edn-modal-header-title">Sales Order</span>
          <button className="edn-modal-close-btn" onClick={onClose} type="button" aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <div className="edn-modal-body">

          <div className="edn-doc-preview">
            <div className="eso-doc-header">
              <span className="eso-doc-title">Sales Order</span>
              <div className="edn-doc-meta-cell eso-doc-so-number">
                <span className="edn-doc-meta-label">SO No.:</span>
                <span className="edn-doc-meta-value">{soNo}</span>
              </div>
            </div>

            <div className="edn-doc-meta">
              <div className="edn-doc-meta-row">
                <div className="edn-doc-meta-cell">
                  <span className="edn-doc-meta-label">Order Type:</span>
                  <span className="edn-doc-meta-value">{order.orderType ?? "Regular Order"}</span>
                </div>
                <div className="edn-doc-meta-cell">
                  <span className="edn-doc-meta-label">Date Issued:</span>
                  <span className="edn-doc-meta-value">{formatDate(new Date().toISOString())}</span>
                </div>
              </div>
              <div className="edn-doc-meta-row">
                <div className="edn-doc-meta-cell">
                  <span className="edn-doc-meta-label">Merchant Name:</span>
                  <span className="edn-doc-meta-value">{order.merchant?.name ?? order.merchantName ?? "-"}</span>
                </div>
                <div className="edn-doc-meta-cell">
                  <span className="edn-doc-meta-label">Store Code:</span>
                  <span className="edn-doc-meta-value">{order.facility?.code ?? order.facilityCode ?? "-"}</span>
                </div>
              </div>
              <div className="edn-doc-meta-row">
                <div className="edn-doc-meta-cell">
                  <span className="edn-doc-meta-label">Location:</span>
                  <span className="edn-doc-meta-value">{order.facility?.name ?? order.facilityName ?? "-"}</span>
                </div>
                <div className="edn-doc-meta-cell">
                  <span className="edn-doc-meta-label">PR No.:</span>
                  <span className="edn-doc-meta-value">{managerApproval.prNo ?? "-"}</span>
                </div>
              </div>
            </div>

            <div className="edn-doc-table-wrap">
              <table className="edn-doc-table">
                <thead>
                  <tr className="edn-doc-table-col-header">
                    <th>No.</th>
                    <th>SKU</th>
                    <th>Item Description</th>
                    <th>UOM</th>
                    <th>QTY</th>
                    <th>Unit Price</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {soItems.map((item, idx) => {
                    const qty = Number(item.quantity ?? item.qty ?? 0);
                    const price = Number(item.unitPrice ?? 0);
                    return (
                      <tr key={idx} className={idx % 2 !== 0 ? "edn-doc-table-row--alt" : ""}>
                        <td>{idx + 1}</td>
                        <td>{item.sku ?? item.productSku ?? "-"}</td>
                        <td>{item.description ?? item.productName ?? item.name ?? "-"}</td>
                        <td>{item.uomName ?? item.UomName ?? item.uom ?? "-"}</td>
                        <td>{qty}</td>
                        <td>{formatCurrency(price)}</td>
                        <td>{formatCurrency(qty * price)}</td>
                      </tr>
                    );
                  })}
                  {Array.from({ length: emptyCount }, (_, i) => (
                    <tr key={`empty-${i}`} className={(soItems.length + i) % 2 !== 0 ? "edn-doc-table-row--alt" : ""}>
                      <td>&nbsp;</td><td></td><td></td><td></td><td></td><td></td><td></td>
                    </tr>
                  ))}
                  <tr className="eso-doc-table-total-row">
                    <td></td>
                    <td></td>
                    <td className="eso-doc-table-total-label">TOTAL</td>
                    <td></td>
                    <td>{totalQty}</td>
                    <td></td>
                    <td>{formatCurrency(totalPrice)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="edn-doc-sig-grid">
              <div className="edn-doc-sig-cell">
                <span className="edn-doc-sig-label">Processed by</span>
                <span className="edn-doc-sig-value">{processedBy || "Employee Name Here"}</span>
              </div>
              <div className="edn-doc-sig-cell">
                <span className="edn-doc-sig-label">Noted by</span>
                <span className="edn-doc-sig-value">General Manager Name Here</span>
              </div>
              <div className="edn-doc-sig-cell">
                <span className="edn-doc-sig-label">Certified Correct</span>
                <span className="edn-doc-sig-value">{certifiedCorrect || "-"}</span>
              </div>
              <div className="edn-doc-sig-cell">
                <span className="edn-doc-sig-label">Received Payment</span>
                <span className="edn-doc-sig-value">{receivedPayment || "-"}</span>
              </div>
            </div>

            <p className="edn-doc-page-footer">PAGE 1 OUT OF X</p>
          </div>

          <div className="edn-form edn-form--spaced">
            <div className="edn-form-row">
              <div className="edn-form-group">
                <label className="edn-form-label">Attachment</label>
                {attachment ? (
                  <div className="edn-attachment-chip">
                    <Paperclip size={14} />
                    <span className="edn-attachment-name" title={attachment.name}>
                      {attachment.name.length > 22
                        ? attachment.name.slice(0, 9) + "…"
                        : attachment.name}
                    </span>
                    <span className="edn-attachment-full">{attachment.name}</span>
                  </div>
                ) : (
                  <span style={{ fontSize: "0.82rem", color: "#9ca3af" }}>No attachment</span>
                )}
              </div>

              <div className="edn-form-group">
                <label className="edn-form-label">Processed by</label>
                <input
                  type="text"
                  className="edn-form-input"
                  value={processedBy}
                  readOnly
                  style={{ background: "#f9fafb", cursor: "not-allowed" }}
                />
              </div>
            </div>

            <div className="edn-form-group">
              <label className="edn-form-label">Remarks</label>
              <textarea
                className="edn-form-textarea"
                rows={3}
                value={remarks}
                readOnly
                style={{ background: "#f9fafb", cursor: "not-allowed" }}
              />
            </div>
          </div>

        </div>

      </div>
    </div>,
    document.body
  );
};

export default SalesOrderViewModal;
